import { useState, useDeferredValue } from 'react';
import { Search, Copy, ExternalLink, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminSearchOrgs,
  useAdminOrgDetail,
  type OrgSearchResult,
  type OrgDetailData,
} from '@/hooks/admin/useAdminOrgLookup';

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="h-6 px-1.5 text-xs gap-1 text-wt-text-muted">
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      {label}
    </Button>
  );
}

function OrgResultsList({ results, selectedId, onSelect }: {
  results: OrgSearchResult[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (results.length === 0) return <p className="text-xs text-wt-text-muted py-4 text-center">No results</p>;
  return (
    <div className="space-y-1">
      {results.map(org => (
        <button
          key={org.id}
          onClick={() => onSelect(org.id)}
          className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
            selectedId === org.id ? 'bg-wt-bg-secondary border border-wt-border' : 'hover:bg-wt-bg-secondary/50'
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="font-medium">{org.name}</span>
            <Badge variant="secondary" className="text-[10px]">{org.property_count} properties</Badge>
          </div>
          {org.billing_email && <p className="text-wt-text-muted mt-0.5">{org.billing_email}</p>}
        </button>
      ))}
    </div>
  );
}

function OrgDetail({ data }: { data: OrgDetailData }) {
  const o = data.org;
  if (!o) return <p className="text-xs text-wt-text-muted py-4">Organization not found</p>;

  const stripeUrl = o.stripe_connect_account_id
    ? `https://dashboard.stripe.com/connect/accounts/${o.stripe_connect_account_id}`
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{o.name}</h3>
        <CopyButton value={o.id} label="ID" />
        {stripeUrl && (
          <a href={stripeUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline">
            <ExternalLink className="h-3 w-3" /> Stripe
          </a>
        )}
      </div>

      {/* Info */}
      <Card className="border-wt-border bg-wt-bg-secondary">
        <CardContent className="pt-3 pb-3 px-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div><span className="text-wt-text-muted">Type:</span> {o.org_type}</div>
          <div><span className="text-wt-text-muted">Created:</span> {new Date(o.created_at).toLocaleDateString()}</div>
          <div><span className="text-wt-text-muted">Billing:</span> {o.billing_status ?? '—'}</div>
          <div><span className="text-wt-text-muted">Default Tier:</span> {o.default_tier ?? '—'}</div>
          <div><span className="text-wt-text-muted">Connect Charges:</span>{' '}
            <Badge variant={o.stripe_connect_charges_enabled ? 'default' : 'destructive'} className="text-[10px]">
              {o.stripe_connect_charges_enabled ? 'Yes' : 'No'}
            </Badge>
          </div>
          <div><span className="text-wt-text-muted">Connect Payouts:</span>{' '}
            <Badge variant={o.stripe_connect_payouts_enabled ? 'default' : 'destructive'} className="text-[10px]">
              {o.stripe_connect_payouts_enabled ? 'Yes' : 'No'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Users */}
      <Card className="border-wt-border bg-wt-bg-secondary">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-xs font-medium">Users ({data.users.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {data.users.length === 0 ? <p className="text-xs text-wt-text-muted">None</p> : (
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Email</th>
                <th className="text-left py-1 pr-2 font-medium">Name</th>
                <th className="text-left py-1 pr-2 font-medium">Role</th>
                <th className="text-left py-1 pr-2 font-medium">Perm</th>
                <th className="text-left py-1 font-medium">Approved</th>
              </tr></thead>
              <tbody>
                {data.users.map(u => (
                  <tr key={u.user_id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{u.email}</td>
                    <td className="py-1 pr-2">{u.full_name ?? '—'}</td>
                    <td className="py-1 pr-2">{u.role}</td>
                    <td className="py-1 pr-2">{u.permission_role ?? '—'}</td>
                    <td className="py-1">
                      <Badge variant={u.approved ? 'default' : 'destructive'} className="text-[10px]">
                        {u.approved ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Properties */}
      <Card className="border-wt-border bg-wt-bg-secondary">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-xs font-medium">Properties ({data.properties.length})</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {data.properties.length === 0 ? <p className="text-xs text-wt-text-muted">None</p> : (
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Name</th>
                <th className="text-left py-1 pr-2 font-medium">Readiness</th>
                <th className="text-left py-1 pr-2 font-medium">Data Source</th>
                <th className="text-left py-1 font-medium">Onboarded</th>
              </tr></thead>
              <tbody>
                {data.properties.map(p => (
                  <tr key={p.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">
                      <a href={`/app/property/${p.id}`} className="text-blue-400 hover:underline">{p.name}</a>
                    </td>
                    <td className="py-1 pr-2">{p.readiness_state ?? '—'}</td>
                    <td className="py-1 pr-2">{p.data_source_mode ?? '—'}</td>
                    <td className="py-1">
                      <Badge variant={p.onboarding_complete ? 'default' : 'secondary'} className="text-[10px]">
                        {p.onboarding_complete ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Service Plans */}
      {data.service_plans.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Service Plans ({data.service_plans.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Name</th>
                <th className="text-left py-1 pr-2 font-medium">Price</th>
                <th className="text-left py-1 pr-2 font-medium">Interval</th>
                <th className="text-left py-1 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {data.service_plans.map(sp => (
                  <tr key={sp.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{sp.name} {sp.is_default && <Badge variant="outline" className="text-[10px] ml-1">default</Badge>}</td>
                    <td className="py-1 pr-2 font-mono">${(sp.price_cents / 100).toFixed(2)}</td>
                    <td className="py-1 pr-2">{sp.billing_interval}</td>
                    <td className="py-1">
                      <Badge variant={sp.is_active ? 'default' : 'secondary'} className="text-[10px]">
                        {sp.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Subscriptions */}
      {data.subscriptions.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Subscriptions ({data.subscriptions.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Property</th>
                <th className="text-left py-1 pr-2 font-medium">Plan</th>
                <th className="text-left py-1 pr-2 font-medium">Status</th>
                <th className="text-left py-1 font-medium">Price</th>
              </tr></thead>
              <tbody>
                {data.subscriptions.map(s => (
                  <tr key={s.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{s.property_name}</td>
                    <td className="py-1 pr-2">{s.plan_name}</td>
                    <td className="py-1 pr-2">
                      <Badge variant={s.status === 'active' ? 'default' : 'destructive'} className="text-[10px]">{s.status}</Badge>
                    </td>
                    <td className="py-1 font-mono">${(s.price_cents / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent Issues */}
      {data.recent_issues.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Recent Issues</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Title</th>
                <th className="text-left py-1 pr-2 font-medium">Property</th>
                <th className="text-left py-1 pr-2 font-medium">Severity</th>
                <th className="text-left py-1 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {data.recent_issues.map(i => (
                  <tr key={i.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{i.title}</td>
                    <td className="py-1 pr-2">{i.property_name}</td>
                    <td className="py-1 pr-2"><Badge variant="outline" className="text-[10px]">{i.severity}</Badge></td>
                    <td className="py-1"><Badge variant="secondary" className="text-[10px]">{i.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent Imports */}
      {data.recent_imports.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Recent Imports</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Provider</th>
                <th className="text-left py-1 pr-2 font-medium">Status</th>
                <th className="text-left py-1 pr-2 font-medium">Plants</th>
                <th className="text-left py-1 font-medium">Created</th>
              </tr></thead>
              <tbody>
                {data.recent_imports.map(ij => (
                  <tr key={ij.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2 font-mono">{ij.provider_type}</td>
                    <td className="py-1 pr-2">
                      <Badge variant={ij.status === 'completed' ? 'default' : ij.status === 'failed' ? 'destructive' : 'secondary'} className="text-[10px]">
                        {ij.status}
                      </Badge>
                    </td>
                    <td className="py-1 pr-2">{ij.processed_plants}/{ij.total_plants}</td>
                    <td className="py-1 text-wt-text-muted">{new Date(ij.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function OrgLookupTab() {
  const [query, setQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query);

  const search = useAdminSearchOrgs(deferredQuery);
  const detail = useAdminOrgDetail(selectedOrgId);

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-wt-text-muted" />
        <Input
          placeholder="Search by org name, billing email, user email, or Stripe Connect ID..."
          value={query}
          onChange={e => { setQuery(e.target.value); setSelectedOrgId(null); }}
          className="pl-9 text-sm"
        />
      </div>

      {deferredQuery.trim().length >= 2 && (
        <AdminTabShell isLoading={search.isLoading} error={search.error}>
          <OrgResultsList
            results={search.data ?? []}
            selectedId={selectedOrgId}
            onSelect={setSelectedOrgId}
          />
        </AdminTabShell>
      )}

      {selectedOrgId && (
        <div className="mt-4 border-t border-wt-border pt-4">
          <AdminTabShell isLoading={detail.isLoading} error={detail.error}>
            {detail.data && <OrgDetail data={detail.data} />}
          </AdminTabShell>
        </div>
      )}
    </div>
  );
}
